import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaEnvelope } from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import { placeholderImage } from '../utils/placeholderImage';
import VariationSelector from '../components/VariationSelector';
import { fetchPopularItems } from '../services/menuService';

const HomePage = () => {
  const [email, setEmail] = useState('');
  const [selectedVariations, setSelectedVariations] = useState({});
  const [featuredItems, setFeaturedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addItem } = useCart();

  // Fetch popular items from Firebase
  useEffect(() => {
    const fetchFeaturedItems = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const popularItems = await fetchPopularItems();
        setFeaturedItems(popularItems);
        
        // Initialize selectedVariations with default variations for items that have variations
        const initialVariations = {};
        popularItems.forEach(item => {
          if (item.variations && item.variations.length > 0) {
            initialVariations[item.id] = item.variations[0]; // Set first variation as default
          }
        });
        setSelectedVariations(initialVariations);
        
      } catch (err) {
        console.error('Error fetching featured items:', err);
        setError('Failed to load featured dishes. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedItems();
  }, []);

  const testimonials = [
    {
      id: 1,
      name: 'Sarah M.',
      rating: 5,
      text: 'The best Sri Lankan food in Perth! The hoppers are absolutely divine and the curry is perfectly spiced.',
      location: 'Willetton'
    },
    {
      id: 2,
      name: 'David L.',
      rating: 5,
      text: 'Amazing catering service for our wedding. Everyone loved the authentic flavors and professional service.',
      location: 'Perth'
    },
    {
      id: 3,
      name: 'Priya K.',
      rating: 5,
      text: 'Finally found authentic Sri Lankan cuisine! The fish curry reminds me of home. Highly recommended!',
      location: 'Canning Vale'
    }
  ];

  const handleVariationSelect = (itemId, variation) => {
    setSelectedVariations(prev => ({
      ...prev,
      [itemId]: variation
    }));
  };

  const handleAddToCart = (item) => {
    const variation = selectedVariations[item.id];
    const itemToAdd = variation 
      ? { ...item, name: `${item.name} - ${variation.name}`, price: variation.price }
      : item;
    addItem(itemToAdd);
  };

  const handleNewsletterSignup = (e) => {
    e.preventDefault();
    // TODO: Implement newsletter signup
    alert('Thank you for subscribing to our newsletter!');
    setEmail('');
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <FaStar
        key={index}
        className={index < rating ? 'text-yellow-400' : 'text-gray-300'}
      />
    ));
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero section-full-width" style={{
        backgroundImage: 'url(/images/nagisceyloncatering-hero-550x440-550x440.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}>
        <div className="hero-content">
          <h1>Authentic Sri Lankan Cuisine</h1>
          <p>
            Experience the vibrant flavors of Ceylon with our traditional recipes passed down through generations.
          </p>
          <div className="hero-buttons">
            <Link to="/menu" className="btn btn-outline">
              Order Now
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Dishes Section */}
      <section className="section">
        <div className="section-title">
          <h2>Featured Dishes</h2>
          <p>
            Discover our most popular Sri Lankan specialties, crafted with authentic spices and traditional cooking methods.
          </p>
        </div>

        {loading ? (
          <div className="text-center" style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div>
              <div className="loading-spinner"></div>
              <h3>Loading featured dishes...</h3>
              <p>Please wait while we fetch the latest menu items.</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center">
            <p style={{ color: '#dc3545' }}>{error}</p>
            <button 
              className="btn btn-primary mt-3"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            <div className="menu-grid">
              {featuredItems.map((item) => (
                <div key={item.id} className="menu-item card">
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="menu-item-image"
                      onError={(e) => {
                        e.target.src = placeholderImage;
                      }}
                    />
                  )}
                  <div className="menu-item-content">
                    <h3 className="menu-item-title">{item.name}</h3>
                    <div className="menu-item-price">
                      ${selectedVariations[item.id] 
                        ? selectedVariations[item.id].price.toFixed(2)
                        : item.price.toFixed(2)
                      }
                    </div>
                    <p className="menu-item-description">{item.description}</p>
                    <div className="dietary-tags">
                      {item.dietary.map((tag) => (
                        <span key={tag} className={`dietary-tag ${tag}`}>
                          {tag.charAt(0).toUpperCase() + tag.slice(1)}
                        </span>
                      ))}
                    </div>
                    <VariationSelector
                      item={item}
                      onVariationSelect={(variation) => handleVariationSelect(item.id, variation)}
                      selectedVariation={selectedVariations[item.id]}
                    />
                    <button
                      className="btn btn-primary w-full"
                      onClick={() => handleAddToCart(item)}
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-4">
              <Link to="/menu" className="btn btn-primary">
                View Full Menu
              </Link>
            </div>
          </>
        )}
      </section>

      {/* House Specials & Signature Buffets */}
      <section className="section-full-width" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="section-title">
          <h2>House Specials & Signature Buffets</h2>
          <p>
            Experience our most popular specials and buffet events, perfect for sharing with family and friends.
          </p>
        </div>

        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
          {/* Chef's Special Mix Grill */}
          <div className="card p-3" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '350px' }}>
            <div className="text-center mb-3">
              <h3 style={{ color: '#ff6b35', marginBottom: '0.5rem' }}>CHEF'S SPECIAL MIX GRILL</h3>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff6b35' }}>$150</div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>SHARED PLATTER</div>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Fried rice</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Chicken Maryland</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Sausages/Fried Eggs</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Crabs/Cuttlefish</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Prawns/Mussels</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Tomato/Cucumber</li>
            </ul>
          </div>

          {/* Friday Hopper Night Buffet */}
          <div className="card p-3" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '350px' }}>
            <div className="text-center mb-3">
              <h3 style={{ color: '#ff6b35', marginBottom: '0.5rem' }}>FRIDAY HOPPER NIGHT BUFFET</h3>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff6b35' }}>$35</div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>PER PERSON</div>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Egg & Plain hoppers</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Kottu & Fried Rice</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Chicken & Fish Curry</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Dhal or Potato Curry</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Lunumiris/PolSambol</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Caramelized Onions</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Dessert</li>
            </ul>
          </div>

          {/* Weekend Lunch Buffet */}
          <div className="card p-3" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '350px' }}>
            <div className="text-center mb-3">
              <h3 style={{ color: '#ff6b35', marginBottom: '0.5rem' }}>WEEKEND LUNCH BUFFET</h3>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff6b35' }}>$28</div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>PER PERSON</div>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Rice - Yellow/White/Red</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Chicken/ Fish Curry</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Dhal/Potato Curry</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Veggies</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Papadam</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Dessert</li>
            </ul>
          </div>

          {/* Weekday Lunch Buffet */}
          <div className="card p-3" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '350px' }}>
            <div className="text-center mb-3">
              <h3 style={{ color: '#ff6b35', marginBottom: '0.5rem' }}>WEEKDAY LUNCH BUFFET</h3>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff6b35' }}>$23</div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>PER PERSON</div>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Rice - White/Red</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Chicken/Fish Curry</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Dhal Curry</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Veggies</li>
              <li style={{ padding: '0.25rem 0', borderBottom: '1px solid #eee' }}>Papadam</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-full-width" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="section-title">
          <h2>Why Choose Nagi's Ceylon</h2>
        </div>

        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
          <div className="card p-3 text-center">
            <div className="mb-2">
              <div style={{ 
                width: '60px', 
                height: '60px', 
                borderRadius: '50%', 
                backgroundColor: '#fff3e0', 
                border: '2px solid #ff6b35',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                fontSize: '1.5rem',
                color: '#ff6b35'
              }}>
                🕐
              </div>
            </div>
            <h3>Fresh Daily</h3>
            <p>All dishes prepared fresh daily with locally sourced ingredients.</p>
          </div>

          <div className="card p-3 text-center">
            <div className="mb-2">
              <div style={{ 
                width: '60px', 
                height: '60px', 
                borderRadius: '50%', 
                backgroundColor: '#ffe6e6', 
                border: '2px solid #ff6b35',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                fontSize: '1.5rem',
                color: '#ff6b35'
              }}>
                ❤️
              </div>
            </div>
            <h3>Authentic Recipes</h3>
            <p>Traditional family recipes from Sri Lanka passed down through generations.</p>
          </div>

          <div className="card p-3 text-center">
            <div className="mb-2">
              <div style={{ 
                width: '60px', 
                height: '60px', 
                borderRadius: '50%', 
                backgroundColor: '#e6f7ff', 
                border: '2px solid #ff6b35',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                fontSize: '1.5rem',
                color: '#ff6b35'
              }}>
                📍
              </div>
            </div>
            <h3>Perth Local</h3>
            <p>Proudly serving the Perth community with authentic Sri Lankan flavors.</p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section">
        <div className="section-title">
          <h2>What Our Customers Say</h2>
        </div>

        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="card p-3">
              <div className="d-flex align-center mb-2">
                {renderStars(testimonial.rating)}
              </div>
              <p className="mb-2">{testimonial.text}</p>
              <div className="d-flex justify-between align-center">
                <strong>{testimonial.name}</strong>
                <span style={{ color: '#666' }}>{testimonial.location}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="section-full-width newsletter-section" style={{ backgroundColor: '#ff6b35', color: 'white' }}>
        <div className="section-title">
          <h2 style={{ color: 'white' }}>Get Hopper Specials & Buffet Updates</h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Subscribe to our newsletter for exclusive offers and updates about our special events.
          </p>
        </div>

        <form onSubmit={handleNewsletterSignup} className="text-center">
          <div className="d-flex justify-center" style={{ maxWidth: '400px', margin: '0 auto' }}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                border: 'none',
                borderRadius: '25px 0 0 25px',
                fontSize: '1rem'
              }}
            />
            <button
              type="submit"
              className="btn btn-primary"
              style={{ borderRadius: '0 25px 25px 0' }}
            >
              <FaEnvelope />
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default HomePage; 